import React, { useRef, useEffect, useState } from "react";
import { BASE_URL } from "utils/constant";
import { fetchOrganizationMembers } from "../../../api/design/service";
import { X } from "lucide-react";
import { IoCloseOutline } from "react-icons/io5";
import { useAlert } from "components/UI_components/Alert";

export default function CreateDesignModal({ show, onClose, formData, setFormData, projects, teamMembers, designTypes, onSubmit }) {
  const fileInputRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orgMembers, setOrgMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isCustomType, setIsCustomType] = useState(false);
  const { addAlert } = useAlert();
  // Hooks must be called unconditionally. Only early-return the component after hooks are declared.

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const name = String(file.name || "").toLowerCase();
      const isPdf = name.endsWith(".pdf") || file.type === "application/pdf";
      const isImage = (file.type || "").startsWith("image/") || /\.(png|jpe?g)$/i.test(name);
      if (!isPdf && !isImage) {
        addAlert("Only JPG, PNG, or PDF files are supported", "warning");
        setUploadedFile(null);
        setFormData({ ...formData, uploadedFileName: "" });
        e.target.value = "";
        return;
      }
      setUploadedFile(file);
      setFormData({ ...formData, uploadedFileName: file.name });
    }
  };


  useEffect(() => {
    const fetchSites = async () => {
      try {
        setLoadingSites(true);
        const org = JSON.parse(localStorage.getItem("organization") || "{}");
        const orgId = org?.id;
        if (!orgId) {
          setSites([]);
          return;
        }
        const accessToken = localStorage.getItem("access");
        const res = await fetch(`${BASE_URL}/sites/organizations/${orgId}/sites/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const json = await res.json();
        // response shape might be { data: [...] } or an array directly
        const list = Array.isArray(json) ? json : (json?.data || json?.results || []);
        setSites(list);
      } catch (err) {
        console.error("Failed to load sites", err);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        setLoadingMembers(true);
        const org = JSON.parse(localStorage.getItem("organization") || "{}");
        const orgId = org?.id;
        if (!orgId) {
          setOrgMembers([]);
          return;
        }
        const list = await fetchOrganizationMembers(orgId);
        setOrgMembers(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load organization members", err);
        setOrgMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };
    loadMembers();
  }, []);

  const assignToIds = Array.isArray(formData.assign_to) ? formData.assign_to : [];

  const handleToggleAssignee = (memberId) => {
    const nextIds = assignToIds.includes(memberId)
      ? assignToIds.filter((id) => id !== memberId)
      : [...assignToIds, memberId];

    const display = (orgMembers || [])
      .filter((m) => nextIds.includes(Number(m.id)))
      .map((m) => {
        const fn = m?.user?.first_name || "";
        const ln = m?.user?.last_name || "";
        const full = `${fn} ${ln}`.trim();
        return full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
      })
      .join(", ");
    setFormData({ ...formData, assign_to: nextIds, assignee: display });
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.projectName || !formData.fileType || !formData.siteId || assignToIds.length === 0 || !uploadedFile) {
      addAlert("Please fill all required fields", "warning");
      return;
    }

    try {
      setSubmitting(true);
      const org = JSON.parse(localStorage.getItem("organization") || "{}");
      const accessToken = localStorage.getItem("access");
      let res;
      if (uploadedFile) {
        // send multipart/form-data with file
        const formPayload = new FormData();
        formPayload.append("site", String(formData.siteId));
        formPayload.append("organization", String(org?.id));
        formPayload.append("design_name", formData.name);
        formPayload.append("type", formData.fileType);
        formPayload.append("status", "draft");
        formPayload.append("diagram", uploadedFile);
        // backend expects org-member ids (multiple possible) - send repeated fields
        (assignToIds || []).forEach((id) => {
          if (id || id === 0) formPayload.append("assign_to", String(Number(id)));
        });

        res = await fetch(`${BASE_URL}/design/designs/create/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formPayload,
        });
      } else {
        const payload = {
          site: Number(formData.siteId),
          organization: Number(org?.id),
          design_name: formData.name,
          type: formData.fileType,
          diagram: null,
          status: "draft",
          assign_to: assignToIds.map((x) => Number(x)),
        };

        res = await fetch(`${BASE_URL}/design/designs/create/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        });
      }
      const data = await res.json();
      if (!res.ok) {
        const errMsg = data?.detail || data?.message || "Failed to create design";
        addAlert(errMsg, "error");
        return;
      }
      // find selected site object to pass back for UI update
      const selectedSite = sites.find(s => String(s.id) === String(formData.siteId));
      // pass created design and the original small formData so parent can update UI
      onSubmit(
        data,
        {
          name: formData.name,
          fileType: formData.fileType,
          // keep a friendly display string for list UI; detail page will re-fetch from members API
          assignee: formData.assignee || "Unassigned",
          assign_to: assignToIds,
        },
        selectedSite
      );
      setFormData({ name: "", projectName: "", assignee: "", assign_to: [], fileType: "", uploadedFileName: "", siteId: "" });
      setUploadedFile(null);
      setIsCustomType(false);
      onClose();
      addAlert("Design Created Successfully", "success");
    } catch (error) {
      console.error("Create design error:", error);
      addAlert("Failed to create design", "error");
    } finally {
      setSubmitting(false);
    }
  };
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded w-full max-w-lg h-[75vh] overflow-y-scroll"
        style={{
          scrollbarWidth: "none",
        }}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Create New Design</h2>
          <button className="text-gray-500 cursor-pointer hover:text-gray-700" onClick={onClose}>
            <IoCloseOutline size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Design Name *</label>
            <input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border p-2 rounded"
              placeholder="Enter design name"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Select Project *</label>
            <select
              value={formData.siteId || ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selected = sites.find(s => String(s.id) === String(selectedId));
                setFormData({ ...formData, siteId: selectedId, projectName: selected?.site_name || "" });
              }}
              className="w-full border p-2 rounded"
            >
              <option value="">{loadingSites ? "Loading projects..." : "Select a project"}</option>
              {sites.map((project) => <option key={project.id} value={project.id}>{project.site_name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Assignee *</label>
            <div className="w-full border rounded p-2 max-h-48 overflow-y-auto space-y-1 bg-white">
              {(orgMembers || []).length === 0 ? (
                <div className="text-sm text-gray-500 py-2 px-1 text-center">
                  {loadingMembers ? "Loading members..." : "No members found"}
                </div>
              ) : (
                (orgMembers || []).map((m) => {
                  const fn = m?.user?.first_name || "";
                  const ln = m?.user?.last_name || "";
                  const full = `${fn} ${ln}`.trim();
                  const label = full || m?.user?.username || m?.user?.email || `Member ${m?.id}`;
                  const isChecked = assignToIds.includes(m.id);
                  return (
                    <label key={m.id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleAssignee(m.id)}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      />
                      <span className={`text-sm ${isChecked ? "text-gray-900 font-medium" : "text-gray-600"}`}>
                        {label}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider font-semibold">
              Selected: {formData.assignee || "None"}
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Type *</label>
            {!isCustomType ? (
              <select
                value={formData.fileType}
                onChange={(e) => {
                  if (e.target.value === "custom_add") {
                    setIsCustomType(true);
                    setFormData({ ...formData, fileType: "" });
                  } else {
                    setFormData({ ...formData, fileType: e.target.value });
                  }
                }}
                className="w-full border p-2 rounded"
              >
                <option value="">Select type</option>
                {designTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="custom_add" className="text-purple-600 font-medium">+ Add Custom Type</option>
              </select>
            ) : (
              <div className="flex gap-2">
                <input
                  value={formData.fileType}
                  onChange={(e) => setFormData({ ...formData, fileType: e.target.value })}
                  className="w-full border p-2 rounded focus:ring-1 focus:ring-purple-500 outline-none"
                  placeholder="Enter custom type"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsCustomType(false);
                    setFormData({ ...formData, fileType: "" });
                  }}
                  className="px-3 border rounded text-gray-500 hover:bg-gray-50 transition-colors"
                  title="Back to list"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2">Upload Diagram *</label>
            <div
              className="border-2 border-dashed rounded p-6 flex flex-col items-center justify-center text-center text-sm text-gray-500 cursor-pointer"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <div className="text-2xl mb-2">⬆️</div>
              <div>Click or drag to upload</div>
              <div className="text-xs text-gray-400 mt-1">Supports: JPG, PNG, PDF</div>
              {formData.uploadedFileName && <div className="text-xs text-gray-600 mt-2">{formData.uploadedFileName}</div>}
              <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={handleFileSelect} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button className="px-4 py-2 border rounded" onClick={onClose}>Cancel</button>
            <button className="px-4 py-2 bg-purple-700 text-white rounded" onClick={handleCreate} disabled={submitting}>{submitting ? "Creating..." : "Create Design"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


